import type { Line, CompletedTask, Tag, Workspace, WorkspaceMember, WorkspaceInvitation, ClaudeActivityLog, Comment, Memo } from "@/lib/types"
import { createClient } from "@/utils/supabase/client"
import { deriveMemoTitle } from "@/lib/memo-utils"
import { buildLineUpsertBatches, buildPartialLineUpdatePayload } from "@/lib/line-upsert-payload"
import { parseRepeatFromRow } from "@/lib/repeat-utils"
import { parseReminderFromRow } from "@/lib/reminders"
import { buildMemoImagePath, buildTaskImagePath, validateTaskImageFile } from "@/lib/task-image"
import { validateTaskFileFile } from "@/lib/task-file"
import { assertRetroScope, mapRetrospectiveRow, type RetroDraft, type RetroMode, type RetroScope, type RetrospectiveRecord } from './retrospective'
import { serializeCompletedNote, deserializeCompletedNote } from "@/lib/completed-task-notes"
import { parseInvitePreview, type InvitePreview } from "@/lib/invite-preview"
import type { InquiryDraft, InquirySource } from "@/lib/inquiry"
import { isTauri } from "@/lib/utils"


// リトライ設定
const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  INITIAL_DELAY: 1000, // 1秒
  MAX_DELAY: 10000,    // 10秒
  BACKOFF_FACTOR: 2,   // 指数バックオフ
}

// @supabase/ssr を使用したクライアント（Cookieセッションに対応）
function getSupabase() {
  return createClient()
}

// リトライ可能なエラーかどうか判定
function isRetryableError(error: any): boolean {
  const message = error?.message || ''
  const code = error?.code || ''

  // ネットワークエラー
  if (message.includes('fetch') || message.includes('network') || message.includes('Failed to fetch')) {
    return true
  }

  // 一時的なサーバーエラー
  if (code.startsWith('5') || code === '429') {
    return true
  }

  // タイムアウト
  if (message.includes('timeout')) {
    return true
  }

  return false
}

// リトライラッパー関数
async function withRetry<T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries = RETRY_CONFIG.MAX_RETRIES
): Promise<T> {
  let lastError: any
  let delay = RETRY_CONFIG.INITIAL_DELAY

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation()
    } catch (error: any) {
      lastError = error

      // リトライ不可のエラーはすぐに失敗
      if (!isRetryableError(error)) {
        throw error
      }

      // 最後の試行の場合はエラーをスロー
      if (attempt === maxRetries) {
        throw error
      }

      // バックオフ待機
      await new Promise(resolve => setTimeout(resolve, delay))

      // 次回の待機時間を計算（指数バックオフ）
      delay = Math.min(delay * RETRY_CONFIG.BACKOFF_FACTOR, RETRY_CONFIG.MAX_DELAY)
    }
  }

  throw lastError
}

// データベース初期化（Supabaseではマイグレーションで済んでいるので不要）
export async function initDatabase() {
  return { success: true, message: "Database already initialized via Supabase migrations" }
}

// ノートブック取得（指定ワークスペースのみ）
// since を指定すると updated_at > since の行のみ返す（差分フェッチ）
export async function getNotebooks(workspaceId: string, since?: number) {
  if (!workspaceId) {
    throw new Error("getNotebooks requires a workspaceId")
  }

  const supabase = getSupabase()

  // getSessionを使用（getUserはサーバーに問い合わせるため遅い）
  const { data: { session }, error: sessionError } = await supabase.auth.getSession()

  if (sessionError) {
    console.error('[supabase-client] Session error:', sessionError)
    throw new Error(`Session error: ${sessionError.message}`)
  }

  if (!session?.user) {
    console.error('[supabase-client] No valid session or user')
    throw new Error("Unauthorized - no valid session")
  }

  // セッションの有効期限をチェック・期限切れならリフレッシュを試みる
  const expiresAt = session.expires_at
  if (expiresAt && expiresAt * 1000 < Date.now()) {
    console.log('[supabase-client] Session expired, attempting refresh...')
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
    if (refreshError || !refreshData.session) {
      console.error('[supabase-client] Session refresh failed:', refreshError)
      throw new Error("Session expired and refresh failed")
    }
    // リフレッシュ成功 - 新しいセッションで続行
    return getNotebooks(workspaceId, since)
  }

  let query = supabase
    .from('notebooks')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })

  if (typeof since === 'number' && since > 0) {
    query = query.gt('updated_at', since)
  }

  const { data, error } = await query

  if (error) {
    console.error('[supabase-client] Error fetching notebooks:', error)
    // 認証エラーの場合は特別なエラーをスロー
    if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
      throw new Error("Authentication error - please sign in again")
    }
    throw new Error(error.message)
  }

  const notebooks = (data || []).map((nb: any) => ({
    id: nb.id,
    name: nb.name,
    isCompleted: nb.is_completed,
    createdAt: nb.created_at,
    updatedAt: nb.updated_at,
    displayOrder: nb.display_order ?? 0,
    color: nb.color ?? undefined,
    workspaceId: nb.workspace_id,
  }))

  return notebooks
}

// ワークスペース内の全ノートブックIDを取得（削除検出用）
export async function getNotebookIds(workspaceId: string): Promise<string[]> {
  if (!workspaceId) {
    throw new Error("getNotebookIds requires a workspaceId")
  }
  const supabase = getSupabase()
  const { data, error } = await supabase
    .from('notebooks')
    .select('id')
    .eq('workspace_id', workspaceId)
  if (error) {
    console.error('[supabase-client] Error fetching notebook ids:', error)
    throw new Error(error.message)
  }
  return (data || []).map((row: any) => row.id)
}

// ノートブック作成
export async function createNotebook(
  notebook: { id: string; name: string; isCompleted?: boolean; createdAt: number },
  workspaceId: string
) {
  // 空のIDを防ぐバリデーション
  if (!notebook.id || notebook.id.trim() === '') {
    console.error('[supabase-client] Cannot create notebook with empty ID')
    throw new Error("Cannot create notebook with empty ID")
  }
  if (!workspaceId) {
    throw new Error("createNotebook requires a workspaceId")
  }

  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }
  const user = session.user

  // まず既存レコードを確認（RLSポリシーにより、ユーザーが所有するノートブックのみ取得可能）
  const { data: existing, error: checkError } = await supabase
    .from('notebooks')
    .select('*')
    .eq('id', notebook.id)
    .eq('user_id', user.id)
    .maybeSingle()

  // チェックエラーが発生した場合でも、続行（RLSポリシーの問題の可能性）
  if (checkError && checkError.code !== 'PGRST116') {
    console.warn('[supabase-client] Error checking existing notebook:', checkError)
  }

  if (existing) {
    // 既に存在する場合は、created_atを更新せずに名前とis_completedのみ更新
    const { data: updateData, error: updateError } = await supabase
      .from('notebooks')
      .update({
        name: notebook.name,
        is_completed: notebook.isCompleted || false,
      })
      .eq('id', notebook.id)
      .eq('user_id', user.id)
      .select()
      .single()

    if (updateError) {
      console.error('[supabase-client] Error updating notebook:', updateError)
      throw new Error(updateError.message)
    }

    return updateData
  }

  // 存在しない場合は新規作成（insertを使用してRLSエラーを回避）
  const { data: insertData, error: insertError } = await supabase
    .from('notebooks')
    .insert({
      id: notebook.id,
      user_id: user.id,
      name: notebook.name,
      is_completed: notebook.isCompleted || false,
      created_at: notebook.createdAt,
      workspace_id: workspaceId,
    })
    .select()
    .single()

  // 重複エラーまたはRLSエラーが発生した場合（PostgreSQLエラーコード23505、HTTPステータス409、または関連メッセージ）
  const isConflictError = insertError && (
    insertError.code === '23505' ||
    insertError.code === '409' ||
    (insertError as any).status === 409 ||
    insertError.message?.toLowerCase().includes('duplicate key') ||
    insertError.message?.toLowerCase().includes('conflict') ||
    insertError.message?.toLowerCase().includes('row-level security') ||
    insertError.message?.toLowerCase().includes('already exists')
  )
  if (isConflictError) {
    // RLSエラーの場合、このIDは別のユーザーに属している可能性がある
    // 自分のノートブックを取得してみる
    const { data: existingData, error: fetchError } = await supabase
      .from('notebooks')
      .select('*')
      .eq('id', notebook.id)
      .eq('user_id', user.id)
      .maybeSingle()

    if (fetchError) {
      console.warn('[supabase-client] Could not fetch notebook:', fetchError.message)
      // IDが別のユーザーに属している場合、nullを返す（エラーをスローしない）
      return null
    }

    if (!existingData) {
      // このIDは別のユーザーに属している - nullを返す
      return null
    }

    // 自分のノートブックが見つかった場合、更新を試みる
    try {
      const { data: updateData, error: updateError } = await supabase
        .from('notebooks')
        .update({
          name: notebook.name,
          is_completed: notebook.isCompleted || false,
        })
        .eq('id', notebook.id)
        .eq('user_id', user.id)
        .select()
        .single()

      if (!updateError && updateData) {
        return updateData
      }
    } catch (updateErr) {
      console.warn('[supabase-client] Error updating notebook after conflict:', updateErr)
    }

    return existingData
  }

  if (insertError) {
    console.error('[supabase-client] Error creating notebook:', insertError)
    throw new Error(insertError.message)
  }

  return insertData
}

// ノートブック更新
export async function updateNotebook(notebook: { id: string; name: string; isCompleted?: boolean }) {
  const supabase = getSupabase()
  const { data, error } = await supabase
    .from('notebooks')
    .update({
      name: notebook.name,
      is_completed: notebook.isCompleted,
    })
    .eq('id', notebook.id)
    .select()
    .single()

  if (error) {
    console.error('[supabase-client] Error updating notebook:', error)
    throw new Error(error.message)
  }

  return data
}

// ノートブックの色を更新(nullで未設定に戻す)
export async function updateNotebookColor(notebookId: string, color: string | null) {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { error } = await supabase
    .from('notebooks')
    .update({ color })
    .eq('id', notebookId)
    .eq('user_id', session.user.id)

  if (error) {
    console.error('[supabase-client] Error updating notebook color:', error)
    throw new Error(error.message)
  }
}

// ノートブックの並び順を更新
export async function updateNotebooksDisplayOrder(notebookOrders: { id: string; displayOrder: number }[]) {
  const supabase = getSupabase()

  // 認証チェック
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    console.warn('[supabase-client] No authenticated user for display order update')
    return { success: false }
  }
  const userId = session.user.id

  // 並列実行でdisplayOrderを更新（パフォーマンス改善）
  const updatePromises = notebookOrders.map(({ id, displayOrder }) =>
    supabase
      .from('notebooks')
      .update({ display_order: displayOrder })
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .then(({ error }) => {
        if (error) {
          console.error('[supabase-client] Error updating display order:', { id, displayOrder, error })
        }
      })
  )

  await Promise.all(updatePromises)

  return { success: true }
}

// ノートブック削除
export async function deleteNotebook(id: string) {
  const supabase = getSupabase()
  const { error } = await supabase
    .from('notebooks')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('[supabase-client] Error deleting notebook:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

// ライン取得
// since を指定すると updated_at > since の行のみ返す（差分フェッチ）
export async function getLines(notebookId: string, since?: number): Promise<Line[]> {
  const supabase = getSupabase()

  let query = supabase
    .from('lines')
    .select('*')
    .eq('notebook_id', notebookId)
    .order('line_order', { ascending: true })

  if (typeof since === 'number' && since > 0) {
    query = query.gt('updated_at', since)
  }

  const { data, error } = await query

  if (error) {
    console.error('[supabase-client] Error fetching lines:', error)
    throw new Error(error.message)
  }

  // 注意: 空の行もそのまま返す（自動削除しない）
  // 空のcontentをフィルタリングすると、ユーザーが入力中のタスクが消えてしまう
  return (data || [])
    .map((line: any) => ({
      id: line.id,
      type: line.type,
      content: line.content || '',
      status: line.status,
      duration: line.duration,
      parentTaskId: line.parent_task_id,
      createdAt: line.created_at,
      updatedAt: line.updated_at,
      bulletPoint: line.bullet_point,
      indentLevel: line.indent_level,
      listNumber: line.list_number,
      scheduledDate: line.scheduled_date,
      startedAt: line.started_at,
      notes: line.notes || '',
      priority: line.priority || undefined,
      tags: line.tags || [],
      assignee: line.assignee ?? undefined,
      assigneeUserId: line.assignee_user_id ?? undefined,
      repeat: parseRepeatFromRow(line.repeat),
      reminder: parseReminderFromRow(line.reminder),
      sortOrder: line.sort_order ?? undefined,
      goalId: line.goal_id ?? undefined,
      notebookId: line.notebook_id,
    }))
}

// ノートブックの全 line ID を取得（削除検出用）
export async function getLineIds(notebookId: string): Promise<string[]> {
  const supabase = getSupabase()
  const { data, error } = await supabase
    .from('lines')
    .select('id')
    .eq('notebook_id', notebookId)
  if (error) {
    console.error('[supabase-client] Error fetching line ids:', error)
    throw new Error(error.message)
  }
  return (data || []).map((row: any) => row.id)
}

// ライン保存（リトライ付き）
export async function saveLines(
  notebookId: string,
  options: {
    lines?: Line[]
    changedLines?: Line[]
    // 既存行の変更フィールドのみの部分更新。行全体 upsert と違い、
    // 送らないカラム（=ユーザーが触っていないフィールド）を上書きしない
    partialUpdates?: { line: Line; changedFields: (keyof Line)[] }[]
    deletedLineIds?: string[]
    allLines?: Line[]  // 全ての行（line_order計算用）
  }
) {
  return withRetry(async () => {
    const { lines, changedLines, partialUpdates, deletedLineIds, allLines } = options
    const supabase = getSupabase()

    // 認証確認
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) {
      throw new Error("Unauthorized - no valid session")
    }

    // 削除
    if (deletedLineIds && deletedLineIds.length > 0) {
      // 誤った差分検出による大量削除からの防御 (2026-07-21 のタスク全消え事故対策):
      // 一括削除がノートブックの既存行の過半数(かつ10件以上)に及ぶ場合は
      // データ消失事故の可能性が高いとみなして削除をスキップする。
      // 件数確認に失敗した場合も安全側に倒してスキップする。
      const MASS_DELETE_MIN = 10
      let skipDeletes = false
      if (deletedLineIds.length >= MASS_DELETE_MIN) {
        const { count, error: countError } = await supabase
          .from('lines')
          .select('id', { count: 'exact', head: true })
          .eq('notebook_id', notebookId)
        if (countError || count == null || deletedLineIds.length >= count * 0.5) {
          skipDeletes = true
          console.error(
            `[supabase-client] Mass delete blocked for ${notebookId}: ` +
              `${deletedLineIds.length} of ${count ?? 'unknown'} lines`,
            deletedLineIds
          )
        }
      }

      if (!skipDeletes) {
        const { error } = await supabase
          .from('lines')
          .delete()
          .eq('notebook_id', notebookId)
          .in('id', deletedLineIds)

        if (error) {
          console.error('[supabase-client] Error deleting lines:', error)
          throw new Error(error.message)
        }
      }
    }

    // line_order計算用の配列: allLines > lines > インデックス の優先順位
    const orderReference = allLines || lines
    // findIndex を行数分繰り返すと O(n^2) になるため、id→index の Map を事前構築
    const orderIndexById = orderReference
      ? new Map(orderReference.map((l, i) => [l.id, i]))
      : null

    // 更新または挿入するライン
    const linesToSave = changedLines || lines || []

    if (linesToSave.length > 0) {
      // assignee_user_id の有無でバッチを分けて upsert（詳細は line-upsert-payload.ts 参照）
      const { withAssigneeUserId, withoutAssigneeUserId } = buildLineUpsertBatches(
        linesToSave,
        notebookId,
        orderIndexById
      )

      for (const batch of [withoutAssigneeUserId, withAssigneeUserId]) {
        if (batch.length === 0) continue
        const { error } = await supabase
          .from('lines')
          .upsert(batch, { onConflict: 'id' })

        if (error) {
          console.error('[supabase-client] Error saving lines:', error)
          throw new Error(error.message)
        }
      }
    }

    // 既存行の部分更新（変更フィールドのみ）。
    // upsert ではなく UPDATE を使うことで、行が既に削除されていた場合に
    // 不完全な行が INSERT されて復活する事故を防ぐ（0行更新は no-op）。
    if (partialUpdates && partialUpdates.length > 0) {
      for (const { line, changedFields } of partialUpdates) {
        const payload = buildPartialLineUpdatePayload(
          line,
          changedFields,
          orderIndexById ? (orderIndexById.get(line.id) ?? null) : null,
        )
        if (Object.keys(payload).length === 0) continue

        const { error } = await supabase
          .from('lines')
          .update(payload)
          .eq('id', line.id)
          .eq('notebook_id', notebookId)

        if (error) {
          console.error('[supabase-client] Error partially updating line:', line.id, error)
          throw new Error(error.message)
        }
      }
    }

    return { success: true }
  }, `saveLines(${notebookId})`)
}

// 完了タスク取得
// since を指定すると updated_at > since の行のみ返す（差分フェッチ）
export async function getCompletedTasks(notebookId: string, since?: number): Promise<CompletedTask[]> {
  const supabase = getSupabase()
  // 完了タスクを取得（JSONB形式）
  let query = supabase
    .from('completed_tasks')
    .select('*')
    .eq('notebook_id', notebookId)
    .order('completed_at', { ascending: false })

  if (typeof since === 'number' && since > 0) {
    query = query.gt('updated_at', since)
  }

  const { data: tasksData, error: tasksError } = await query

  if (tasksError) {
    console.error('[supabase-client] Error fetching completed tasks:', tasksError)
    throw new Error(tasksError.message)
  }

  if (!tasksData || tasksData.length === 0) {
    return []
  }

  // JSONB からデータを展開して返す
  return tasksData.map((row: any) => {
    const taskData = row.task_data || {}
    const notesData = row.notes_data || []

    return {
      task: {
        id: row.id,
        type: 'task' as const,
        content: taskData.content || '',
        status: taskData.status || 'completed',
        duration: taskData.duration || null,
        createdAt: row.completed_at,
        // JSONB に保存された追加フィールドも復元
        scheduledDate: taskData.scheduledDate,
        indentLevel: taskData.indentLevel,
        bulletPoint: taskData.bulletPoint,
        parentTaskId: taskData.parentTaskId,
        assignee: taskData.assignee,
        assigneeUserId: taskData.assigneeUserId,
        subtaskSortIndex: taskData.subtaskSortIndex,
        sortOrder: taskData.sortOrder,
        goalId: taskData.goalId,
      },
      // 子行の type / status などは notes_data に保存されている。
      // 古い記録 (type 未保存) は task-note にフォールバックする
      // (lib/completed-task-notes.ts)
      notes: notesData.map((note: any) => deserializeCompletedNote(note, row.completed_at)),
      completedAt: row.completed_at,
      // mobile が控えた「元の行 id」。読み落とすと重複排除が mobile 製の記録に
      // 当たらず、さらに書き戻しで消えて mobile 側の照合まで壊れる
      // (lib/completed-task-source.ts)
      originalTaskId: taskData.originalTaskId,
    }
  })
}

// 完了タスク保存（upsert方式でデータ消失を防止）
// 保存中フラグ（レースコンディション防止用）
let isCompletedTasksSaving = false
let pendingCompletedTasksSave: { notebookId: string; completedTasks: CompletedTask[] } | null = null

export async function saveCompletedTasks(notebookId: string, completedTasks: CompletedTask[]) {
  // 既に保存中の場合は、次回の保存用にキューに入れる
  if (isCompletedTasksSaving) {
    pendingCompletedTasksSave = { notebookId, completedTasks }
    return { success: true, queued: true }
  }

  isCompletedTasksSaving = true
  try {
    return await _saveCompletedTasksInternal(notebookId, completedTasks)
  } finally {
    isCompletedTasksSaving = false
    // キューに保存待ちがあれば処理
    if (pendingCompletedTasksSave) {
      const pending = pendingCompletedTasksSave
      pendingCompletedTasksSave = null
      // 非同期で次の保存を実行（awaitしない）
      _saveCompletedTasksInternal(pending.notebookId, pending.completedTasks).catch(err => {
        console.error('[supabase-client] Error saving queued completed tasks:', err)
      })
    }
  }
}

// 単一の完了タスクを保存（増分保存）
export async function saveCompletedTask(notebookId: string, completedTask: CompletedTask) {
  return withRetry(async () => {
    const supabase = getSupabase()

    // 認証確認
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) {
      throw new Error("Unauthorized - no valid session")
    }

    const ct = completedTask
    const taskToInsert = {
      id: ct.task.id,
      notebook_id: notebookId,
      task_data: {
        id: ct.task.id,
        content: ct.task.content,
        status: ct.task.status || 'completed',
        duration: ct.task.duration || null,
        type: 'task',
        scheduledDate: ct.task.scheduledDate,
        indentLevel: ct.task.indentLevel,
        bulletPoint: ct.task.bulletPoint,
        parentTaskId: ct.task.parentTaskId,
        assignee: ct.task.assignee,
        assigneeUserId: ct.task.assigneeUserId,
        subtaskSortIndex: ct.task.subtaskSortIndex,
        sortOrder: ct.task.sortOrder,
        goalId: ct.task.goalId,
        // mobile が完了時に控えた「元の行 id」。ここは明示列挙なので、
        // 載せ忘れると読み戻し→書き戻しの往復で値が消え、mobile 側の
        // 重複排除が壊れる (lib/completed-task-source.ts)
        originalTaskId: ct.originalTaskId,
      },
      // 子行の type (サブタスク / コメント) を落とすと復元時に全部コメントに
      // 化けるため、保存形式は lib/completed-task-notes.ts に集約している
      notes_data: ct.notes.map(serializeCompletedNote),
      completed_at: ct.completedAt,
    }

    const { error } = await supabase
      .from('completed_tasks')
      .upsert(taskToInsert, { onConflict: 'id' })

    if (error) {
      console.error('[supabase-client] Error saving completed task:', error)
      throw new Error(error.message)
    }

    return { success: true }
  }, `saveCompletedTask(${notebookId}, ${completedTask.task.id})`)
}

async function _saveCompletedTasksInternal(notebookId: string, completedTasks: CompletedTask[]) {
  return withRetry(async () => {
    const supabase = getSupabase()

    // 認証確認
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) {
      throw new Error("Unauthorized - no valid session")
    }

    // 空の場合のみ削除（全タスクが未完了に戻された場合）
    if (completedTasks.length === 0) {
      const { error: deleteTasksError } = await supabase
        .from('completed_tasks')
        .delete()
        .eq('notebook_id', notebookId)

      if (deleteTasksError) {
        console.error('[supabase-client] Error deleting completed tasks:', deleteTasksError)
        throw new Error(deleteTasksError.message)
      }
      return { success: true }
    }

    // upsertを使用してデータ消失を防止
    // 重複IDを除去（同じIDが複数ある場合は最後のものを使用）
    const taskMap = new Map<string, typeof completedTasks[0]>()
    for (const ct of completedTasks) {
      if (ct.task && ct.task.id) {
        taskMap.set(ct.task.id, ct)
      } else {
        console.warn('[supabase-client] Skipping invalid completed task:', ct)
      }
    }

    // JSONB形式でデータを構築
    const tasksToUpsertRaw = Array.from(taskMap.values()).map(ct => ({
      id: ct.task.id,
      notebook_id: notebookId,
      task_data: {
        id: ct.task.id,
        content: ct.task.content,
        status: ct.task.status || 'completed',
        duration: ct.task.duration || null,
        type: 'task',
        scheduledDate: ct.task.scheduledDate,
        indentLevel: ct.task.indentLevel,
        bulletPoint: ct.task.bulletPoint,
        parentTaskId: ct.task.parentTaskId,
        assignee: ct.task.assignee,
        assigneeUserId: ct.task.assigneeUserId,
        subtaskSortIndex: ct.task.subtaskSortIndex,
        sortOrder: ct.task.sortOrder,
        goalId: ct.task.goalId,
        // mobile が完了時に控えた「元の行 id」。ここは明示列挙なので、
        // 載せ忘れると読み戻し→書き戻しの往復で値が消え、mobile 側の
        // 重複排除が壊れる (lib/completed-task-source.ts)
        originalTaskId: ct.originalTaskId,
      },
      // 子行の type (サブタスク / コメント) を落とすと復元時に全部コメントに
      // 化けるため、保存形式は lib/completed-task-notes.ts に集約している
      notes_data: ct.notes.map(serializeCompletedNote),
      completed_at: ct.completedAt,
    }))

    // 最終的な重複IDチェック（upsert配列レベルで再度確認）
    const seenIds = new Set<string>()
    const tasksToUpsert = tasksToUpsertRaw.filter(task => {
      if (seenIds.has(task.id)) {
        console.warn(`[supabase-client] Duplicate task ID found in upsert array: ${task.id}`)
        return false
      }
      seenIds.add(task.id)
      return true
    })

    const { error: upsertTasksError } = await supabase
      .from('completed_tasks')
      .upsert(tasksToUpsert, { onConflict: 'id' })

    if (upsertTasksError) {
      console.error('[supabase-client] Error upserting completed tasks:', {
        message: upsertTasksError.message,
        code: upsertTasksError.code,
        details: upsertTasksError.details,
        hint: upsertTasksError.hint,
      })
      console.error('[supabase-client] Tasks being upserted:', JSON.stringify(tasksToUpsert, null, 2))
      throw new Error(upsertTasksError.message)
    }

    // 現在のタスクIDリストに含まれない古いタスクを削除（重複除去後のIDを使用）
    const currentTaskIds = Array.from(taskMap.keys())

    // 空の配列の場合、全削除を避けるためスキップ
    if (currentTaskIds.length > 0) {
      const { error: deleteOldTasksError } = await supabase
        .from('completed_tasks')
        .delete()
        .eq('notebook_id', notebookId)
        .filter('id', 'not.in', `(${currentTaskIds.join(',')})`)

      if (deleteOldTasksError) {
        console.warn('[supabase-client] Error deleting old completed tasks:', deleteOldTasksError)
        // 削除エラーは警告のみ（データ消失より安全）
      }
    }

    return { success: true }
  }, `saveCompletedTasks(${notebookId})`)
}

// DB接続チェック
export function isDatabaseConfigured() {
  return true
}

// ステータス変更履歴を記録
export async function recordStatusChange(
  lineId: string,
  notebookId: string,
  taskContent: string,
  fromStatus: string | null,
  toStatus: string
) {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    console.warn('[supabase-client] No session for recordStatusChange')
    return
  }

  const { error } = await supabase
    .from('task_status_history')
    .insert({
      line_id: lineId,
      notebook_id: notebookId,
      task_content: taskContent,
      from_status: fromStatus,
      to_status: toStatus,
      changed_at: Date.now(),
      user_id: session.user.id
    })

  if (error) {
    console.error('[supabase-client] Error recording status change:', error)
  }
}

// メンバーフィルタオプション
export interface MemberFilterOptions {
  /** 指定された場合、これらのユーザーIDのデータのみ取得。未指定の場合は自分のデータのみ */
  memberUserIds?: string[];
}

// 特定日のステータス変更履歴を取得
//
// workspaceId は必須。getTags / getNotebooks と同じ理由でフォールバックを持たせない:
// 省略を許すと notebook 集合による絞り込みが消え、自分が属する全ワークスペースの
// 履歴が混ざって返る（個人 WS の作業時間がチームの集計に混入する）。
// 呼び出し側は WS が未解決なら取得自体をスキップすること。
export async function getStatusHistoryForDate(
  date: string,
  workspaceId: string,
  options?: MemberFilterOptions
) {
  if (!workspaceId) {
    throw new Error("getStatusHistoryForDate requires a workspaceId")
  }

  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const dayStart = new Date(date).setHours(0, 0, 0, 0)
  const dayEnd = new Date(date).setHours(23, 59, 59, 999)

  // そのワークスペースの notebook に紐づく履歴のみ取得
  const { data: nbRows, error: nbError } = await supabase
    .from('notebooks')
    .select('id')
    .eq('workspace_id', workspaceId)

  if (nbError) {
    console.error('[supabase-client] Error fetching notebooks for workspace:', nbError)
    throw new Error(nbError.message)
  }

  const notebookIds = (nbRows || []).map((r) => r.id)
  if (notebookIds.length === 0) {
    return []
  }

  // メンバーフィルタの適用:
  // - memberUserIds が指定されていて要素がある → 指定メンバーのデータを取得
  // - memberUserIds が空配列 → user_id フィルタなし（RLSポリシーに任せる = 全メンバー）
  // - memberUserIds が未指定 → 自分のデータのみ
  let query = supabase
    .from('task_status_history')
    .select('*')
    .in('notebook_id', notebookIds)
    .gte('changed_at', dayStart)
    .lte('changed_at', dayEnd)
    .order('changed_at', { ascending: true })

  if (options?.memberUserIds !== undefined) {
    if (options.memberUserIds.length > 0) {
      query = query.in('user_id', options.memberUserIds)
    }
    // 空配列の場合は user_id フィルタを適用しない（RLSポリシーで制御）
  } else {
    query = query.eq('user_id', session.user.id)
  }

  const { data, error } = await query

  if (error) {
    console.error('[supabase-client] Error fetching status history:', error)
    throw new Error(error.message)
  }

  return data || []
}

// 期間（YYYY-MM-DD start..end inclusive）の status_history を一括取得
//
// workspaceId は必須（理由は getStatusHistoryForDate と同じ）
export async function getStatusHistoryForRange(
  startDate: string,
  endDate: string,
  workspaceId: string,
  options?: MemberFilterOptions
) {
  if (!workspaceId) {
    throw new Error("getStatusHistoryForRange requires a workspaceId")
  }

  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const rangeStart = new Date(startDate).setHours(0, 0, 0, 0)
  const rangeEnd = new Date(endDate).setHours(23, 59, 59, 999)

  const { data: nbRows, error: nbError } = await supabase
    .from('notebooks')
    .select('id')
    .eq('workspace_id', workspaceId)

  if (nbError) {
    console.error('[supabase-client] Error fetching notebooks for workspace:', nbError)
    throw new Error(nbError.message)
  }

  const notebookIds = (nbRows || []).map((r) => r.id)
  if (notebookIds.length === 0) {
    return []
  }

  // メンバーフィルタの適用:
  // - memberUserIds が指定されていて要素がある → 指定メンバーのデータを取得
  // - memberUserIds が空配列 → user_id フィルタなし（RLSポリシーに任せる = 全メンバー）
  // - memberUserIds が未指定 → 自分のデータのみ
  let query = supabase
    .from('task_status_history')
    .select('*')
    .in('notebook_id', notebookIds)
    .gte('changed_at', rangeStart)
    .lte('changed_at', rangeEnd)
    .order('changed_at', { ascending: true })

  if (options?.memberUserIds !== undefined) {
    if (options.memberUserIds.length > 0) {
      query = query.in('user_id', options.memberUserIds)
    }
    // 空配列の場合は user_id フィルタを適用しない（RLSポリシーで制御）
  } else {
    query = query.eq('user_id', session.user.id)
  }

  const { data, error } = await query

  if (error) {
    console.error('[supabase-client] Error fetching status history range:', error)
    throw new Error(error.message)
  }

  return data || []
}

// 期間内の作業セッションを日付ごとにグループ化して返す
// （getWorkSessionsForDate を範囲対応にしたもの）
// workspaceId は getStatusHistoryForRange へそのまま渡すため必須
export async function getWorkSessionsForRange(
  startDate: string,
  endDate: string,
  workspaceId: string,
  options?: MemberFilterOptions
): Promise<
  Map<
    string,
    {
      lineId: string
      notebookId: string
      taskContent: string
      startedAt: number
      endedAt: number
      endStatus: 'not-started' | 'completed'
      startEntryId?: string
      endEntryId?: string
      userId?: string
    }[]
  >
> {
  const supabase = getSupabase()
  const history = await getStatusHistoryForRange(startDate, endDate, workspaceId, options)

  interface WorkSession {
    lineId: string
    notebookId: string
    taskContent: string
    startedAt: number
    endedAt: number
    endStatus: 'not-started' | 'completed'
    startEntryId?: string
    endEntryId?: string
    userId?: string
  }

  const sessions: WorkSession[] = []
  const activeSessions = new Map<string, {
    startedAt: number
    taskContent: string
    notebookId: string
    startEntryId: string
    userId: string
  }>()

  for (const entry of history) {
    if (entry.to_status === 'in-progress') {
      activeSessions.set(entry.line_id, {
        startedAt: entry.changed_at,
        taskContent: entry.task_content || '',
        notebookId: entry.notebook_id,
        startEntryId: entry.id,
        userId: entry.user_id,
      })
    } else if (
      entry.from_status === 'in-progress' ||
      ((entry.to_status === 'completed' ||
        entry.to_status === 'not-started' ||
        entry.to_status === 'review') &&
        activeSessions.has(entry.line_id))
    ) {
      const open = activeSessions.get(entry.line_id)
      if (open) {
        const endStatus =
          entry.to_status === 'completed' ? 'completed' : 'not-started'
        sessions.push({
          lineId: entry.line_id,
          notebookId: open.notebookId,
          taskContent: open.taskContent,
          startedAt: open.startedAt,
          endedAt: entry.changed_at,
          endStatus,
          startEntryId: open.startEntryId,
          endEntryId: entry.id,
          userId: open.userId,
        })
        activeSessions.delete(entry.line_id)
      }
    }
  }

  // 期間末まで open のままのセッション: 実際に in-progress 状態のもののみ「今」までで計上
  const now = Date.now()
  const rangeEnd = new Date(endDate).setHours(23, 59, 59, 999)
  if (activeSessions.size > 0 && now <= rangeEnd) {
    const lineIds = Array.from(activeSessions.keys())
    let currentlyInProgress = new Set<string>()
    try {
      const { data: lineRows } = await supabase
        .from('lines')
        .select('id, status')
        .in('id', lineIds)
      for (const row of lineRows || []) {
        if ((row as { status?: string }).status === 'in-progress') {
          currentlyInProgress.add((row as { id: string }).id)
        }
      }
    } catch (e) {
      console.warn('[supabase-client] Failed to verify in-progress lines:', e)
      currentlyInProgress = new Set<string>()
    }
    for (const [lineId, open] of activeSessions) {
      if (!currentlyInProgress.has(lineId)) continue
      sessions.push({
        lineId,
        notebookId: open.notebookId,
        taskContent: open.taskContent,
        startedAt: open.startedAt,
        endedAt: now,
        endStatus: 'not-started',
        startEntryId: open.startEntryId,
        userId: open.userId,
      })
    }
  }

  // 日付ごとにグループ化（startedAt の日付で振り分け、日跨ぎセッションは開始日に入れる）
  const byDate = new Map<string, WorkSession[]>()
  for (const s of sessions) {
    const d = new Date(s.startedAt)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const arr = byDate.get(key) ?? []
    arr.push(s)
    byDate.set(key, arr)
  }
  return byDate
}

// 作業セッション（in-progress期間）を算出
// workspaceId は getStatusHistoryForDate へそのまま渡すため必須
export async function getWorkSessionsForDate(
  date: string,
  workspaceId: string,
  options?: MemberFilterOptions
) {
  const history = await getStatusHistoryForDate(date, workspaceId, options)
  const supabase = getSupabase()

  interface WorkSession {
    lineId: string
    notebookId: string
    taskContent: string
    startedAt: number
    endedAt: number
    endStatus: 'not-started' | 'completed'
    startEntryId?: string
    endEntryId?: string
    userId?: string
  }

  const sessions: WorkSession[] = []
  const activeSessions = new Map<string, {
    startedAt: number
    taskContent: string
    notebookId: string
    startEntryId: string
    userId: string
  }>()

  for (const entry of history) {
    if (entry.to_status === 'in-progress') {
      // 作業開始
      activeSessions.set(entry.line_id, {
        startedAt: entry.changed_at,
        taskContent: entry.task_content || '',
        notebookId: entry.notebook_id,
        startEntryId: entry.id,
        userId: entry.user_id,
      })
    } else if (
      // 進行中セッションを閉じる条件:
      //   1) from_status === 'in-progress' の明示的な終了エントリ
      //   2) 開始エントリの後に来た completed/not-started/review エントリ（client 側で
      //      in-progress からの中間記録が抜けた過去データ救済）
      entry.from_status === 'in-progress' ||
      ((entry.to_status === 'completed' ||
        entry.to_status === 'not-started' ||
        entry.to_status === 'review') &&
        activeSessions.has(entry.line_id))
    ) {
      // 作業終了（一時停止または完了）
      const session = activeSessions.get(entry.line_id)
      if (session) {
        const endStatus =
          entry.to_status === 'completed' ? 'completed' : 'not-started'
        sessions.push({
          lineId: entry.line_id,
          notebookId: session.notebookId,
          taskContent: session.taskContent,
          startedAt: session.startedAt,
          endedAt: entry.changed_at,
          endStatus,
          startEntryId: session.startEntryId,
          endEntryId: entry.id,
          userId: session.userId,
        })
        activeSessions.delete(entry.line_id)
      }
    }
  }

  // まだ進行中のセッション（日をまたいで作業中）
  // ただし「真に in-progress 状態のタスク」のみ計上する。
  // client 側で in-progress → completed の遷移が記録される前に終了履歴が抜けたケースを
  // 過剰に表示しないよう、現在実際に in-progress 状態のタスクIDを取得して照合する。
  const now = Date.now()
  const dayEnd = new Date(date).setHours(23, 59, 59, 999)
  if (activeSessions.size > 0 && now <= dayEnd) {
    const lineIds = Array.from(activeSessions.keys())
    let currentlyInProgress = new Set<string>()
    try {
      const { data: lineRows } = await supabase
        .from('lines')
        .select('id, status')
        .in('id', lineIds)
      for (const row of lineRows || []) {
        if ((row as { status?: string }).status === 'in-progress') {
          currentlyInProgress.add((row as { id: string }).id)
        }
      }
    } catch (e) {
      // 取得失敗時は安全側に倒して open セッションを表示しない
      console.warn('[supabase-client] Failed to verify in-progress lines:', e)
      currentlyInProgress = new Set<string>()
    }

    for (const [lineId, session] of activeSessions) {
      if (!currentlyInProgress.has(lineId)) continue
      sessions.push({
        lineId,
        notebookId: session.notebookId,
        taskContent: session.taskContent,
        startedAt: session.startedAt,
        endedAt: now,
        endStatus: 'not-started', // まだ進行中
        startEntryId: session.startEntryId,
        userId: session.userId,
      })
    }
  }

  return sessions
}

// ステータス履歴エントリを更新
export async function updateStatusHistoryEntry(
  entryId: string,
  updates: {
    changed_at?: number
    task_content?: string
    notebook_id?: string
  }
) {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { error } = await supabase
    .from('task_status_history')
    .update(updates)
    .eq('id', entryId)
    .eq('user_id', session.user.id)

  if (error) {
    console.error('[supabase-client] Error updating status history:', error)
    throw new Error(error.message)
  }
}

// セッションを更新（開始時間、終了時間、内容、ノートブック）
export async function updateWorkSession(
  session: {
    startEntryId?: string
    endEntryId?: string
  },
  updates: {
    startedAt?: number
    endedAt?: number
    taskContent?: string
    notebookId?: string
  }
) {
  const promises: Promise<void>[] = []

  // 開始時間または内容/ノートブックの更新
  if (session.startEntryId && (updates.startedAt !== undefined || updates.taskContent !== undefined || updates.notebookId !== undefined)) {
    const startUpdates: { changed_at?: number; task_content?: string; notebook_id?: string } = {}
    if (updates.startedAt !== undefined) startUpdates.changed_at = updates.startedAt
    if (updates.taskContent !== undefined) startUpdates.task_content = updates.taskContent
    if (updates.notebookId !== undefined) startUpdates.notebook_id = updates.notebookId
    promises.push(updateStatusHistoryEntry(session.startEntryId, startUpdates))
  }

  // 終了時間の更新
  if (session.endEntryId && updates.endedAt !== undefined) {
    promises.push(updateStatusHistoryEntry(session.endEntryId, { changed_at: updates.endedAt }))
  }

  // 終了エントリの内容/ノートブックも更新（一貫性のため）
  if (session.endEntryId && (updates.taskContent !== undefined || updates.notebookId !== undefined)) {
    const endUpdates: { task_content?: string; notebook_id?: string } = {}
    if (updates.taskContent !== undefined) endUpdates.task_content = updates.taskContent
    if (updates.notebookId !== undefined) endUpdates.notebook_id = updates.notebookId
    promises.push(updateStatusHistoryEntry(session.endEntryId, endUpdates))
  }

  await Promise.all(promises)
}

// ===== タグ関連の関数 =====

// ワークスペースのタグ取得（指定ワークスペースのみ）
//
// workspaceId は必須。以前は未指定だと `user_id = 自分` へフォールバックしていたが、
// これは「自分が作ったタグ全部」= 個人 WS + 自分が作ったチーム WS を意味し、
// ワークスペース境界を越える（呼び出し側の tag-store が回避していただけで、
// フォールバック自体が残っていると新しい呼び出し元が同じ穴を踏む）。
// getNotebooks と同じく引数で固める。mobile も同種のフォールバックを廃止済み。
export async function getTags(workspaceId: string): Promise<Tag[]> {
  if (!workspaceId) {
    throw new Error("getTags requires a workspaceId")
  }

  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { data, error } = await supabase
    .from('tags')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: true })

  if (error) {
    console.error('[supabase-client] Error fetching tags:', error)
    throw new Error(error.message)
  }

  return (data || []).map((tag: any) => ({
    id: tag.id,
    name: tag.name,
    color: tag.color,
    workspaceId: tag.workspace_id,
  }))
}

// タグ作成
//
// workspaceId は必須。getTags と同じ理由でフォールバックを持たせない:
// - insert 側で workspace_id を省くと、DB の NOT NULL 制約 (scripts/009) に
//   引っかかって必ず失敗する = ユーザーには意味不明なエラーになるだけの死んだ分岐
// - 重複時 (23505) の既存タグ検索を `user_id = 自分` にフォールバックすると
//   「自分が作ったタグ全部」= 別ワークスペースのタグを掴んで返してしまう
export async function createTag(name: string, color: string, workspaceId: string): Promise<Tag> {
  if (!workspaceId) {
    throw new Error("createTag requires a workspaceId")
  }

  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const insertData: { user_id: string; name: string; color: string; workspace_id: string } = {
    user_id: session.user.id,
    name: name.trim(),
    color,
    workspace_id: workspaceId,
  }

  const { data, error } = await supabase
    .from('tags')
    .insert(insertData)
    .select()
    .single()

  if (error) {
    // 重複エラーの場合は既存のタグを返す（同じワークスペース内のものだけ）
    if (error.code === '23505') {
      const { data: existing } = await supabase
        .from('tags')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('name', name.trim())
        .single()

      if (existing) {
        return {
          id: existing.id,
          name: existing.name,
          color: existing.color,
          workspaceId: existing.workspace_id,
        }
      }
    }
    console.error('[supabase-client] Error creating tag:', error)
    throw new Error(error.message)
  }

  return {
    id: data.id,
    name: data.name,
    color: data.color,
    workspaceId: data.workspace_id,
  }
}

// タグ更新
export async function updateTag(id: string, updates: { name?: string; color?: string }): Promise<Tag> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const updateData: { name?: string; color?: string } = {}
  if (updates.name !== undefined) updateData.name = updates.name.trim()
  if (updates.color !== undefined) updateData.color = updates.color

  const { data, error } = await supabase
    .from('tags')
    .update(updateData)
    .eq('id', id)
    .eq('user_id', session.user.id)
    .select()
    .single()

  if (error) {
    console.error('[supabase-client] Error updating tag:', error)
    throw new Error(error.message)
  }

  return {
    id: data.id,
    name: data.name,
    color: data.color,
    workspaceId: data.workspace_id,
  }
}

// タグ削除
export async function deleteTag(id: string): Promise<{ success: boolean }> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { error } = await supabase
    .from('tags')
    .delete()
    .eq('id', id)
    .eq('user_id', session.user.id)

  if (error) {
    console.error('[supabase-client] Error deleting tag:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

// ===== Memos(Memos ページ)=====
// メモ自体は本人のみ閲覧可(RLS は user_id ベース)だが、表示・作成は
// ワークスペース単位で絞る(workspace_id)。workspace_id IS NULL は
// マイグレーション前のレガシー行で、個人 WS でのみ表示する。
// 保存は即時系: sync-manager のバッチ diff を通さない(外部 MCP 書き込みが消されないため)

function mapMemoRow(row: any): Memo {
  return {
    id: row.id,
    notebookId: row.notebook_id ?? undefined,
    workspaceId: row.workspace_id ?? undefined,
    title: row.title,
    content: row.content,
    createdAt: new Date(row.created_at).getTime(),
    updatedAt: new Date(row.updated_at).getTime(),
  }
}

// workspaceId は必須。getTags / getNotebooks と同じ理由でフォールバックを持たせない
export async function getMemos(
  workspaceId: string,
  opts?: { isPersonalWorkspace?: boolean }
): Promise<Memo[]> {
  if (!workspaceId) {
    throw new Error("getMemos requires a workspaceId")
  }
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) throw new Error("Unauthorized")

  let query = supabase
    .from('memos')
    .select('*')
    .eq('user_id', session.user.id)

  // 個人 WS では workspace_id IS NULL のレガシー行(バックフィル漏れ・MCP 経由の
  // 未割当行)も表示する。チーム WS は完全一致のみ
  if (opts?.isPersonalWorkspace) {
    query = query.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`)
  } else {
    query = query.eq('workspace_id', workspaceId)
  }

  const { data, error } = await query.order('updated_at', { ascending: false })

  if (error) {
    console.error('[supabase-client] Error fetching memos:', error)
    throw new Error(error.message)
  }
  return (data || []).map(mapMemoRow)
}

export async function createMemo(input: {
  content?: string
  notebookId?: string
  workspaceId: string
}): Promise<Memo> {
  if (!input.workspaceId) {
    throw new Error("createMemo requires a workspaceId")
  }
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) throw new Error("Unauthorized")

  const content = input.content ?? ""
  const insertData: Record<string, unknown> = {
    user_id: session.user.id,
    workspace_id: input.workspaceId,
    content,
    title: deriveMemoTitle(content),
  }
  if (input.notebookId) insertData.notebook_id = input.notebookId

  const { data, error } = await supabase.from('memos').insert(insertData).select().single()
  if (error) {
    console.error('[supabase-client] Error creating memo:', error)
    throw new Error(error.message)
  }
  return mapMemoRow(data)
}

export async function updateMemo(
  id: string,
  updates: { content?: string; notebookId?: string | null }
): Promise<Memo> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) throw new Error("Unauthorized")

  const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() }
  if (updates.content !== undefined) {
    updateData.content = updates.content
    updateData.title = deriveMemoTitle(updates.content)
  }
  if (updates.notebookId !== undefined) updateData.notebook_id = updates.notebookId

  const { data, error } = await supabase
    .from('memos')
    .update(updateData)
    .eq('id', id)
    .eq('user_id', session.user.id)
    .select()
    .single()

  if (error) {
    console.error('[supabase-client] Error updating memo:', error)
    throw new Error(error.message)
  }
  return mapMemoRow(data)
}

export async function deleteMemo(id: string): Promise<{ success: boolean }> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) throw new Error("Unauthorized")

  const { error } = await supabase.from('memos').delete().eq('id', id).eq('user_id', session.user.id)
  if (error) {
    console.error('[supabase-client] Error deleting memo:', error)
    throw new Error(error.message)
  }
  return { success: true }
}

// ===== ワークスペース関連の関数 =====

// ランダムな招待トークンを生成
function generateInviteToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let token = ''
  for (let i = 0; i < 12; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return token
}

// 参加ワークスペース一覧を取得
export async function getWorkspaces(): Promise<Workspace[]> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  // workspace_membersを経由して自分が所属するワークスペースを取得
  const { data, error } = await supabase
    .from('workspace_members')
    .select(`
      workspace_id,
      workspaces (
        id,
        name,
        type,
        owner_id,
        created_at
      )
    `)
    .eq('user_id', session.user.id)

  if (error) {
    console.error('[supabase-client] Error fetching workspaces:', error)
    throw new Error(error.message)
  }

  return (data || []).map((row: any) => ({
    id: row.workspaces.id,
    name: row.workspaces.name,
    type: row.workspaces.type,
    ownerId: row.workspaces.owner_id,
    createdAt: row.workspaces.created_at,
  }))
}

// ワークスペース作成
export async function createWorkspace(
  name: string,
  type: 'personal' | 'team'
): Promise<Workspace> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  // ワークスペースIDを生成
  const workspaceId = type === 'personal'
    ? `personal-${session.user.id}`
    : `team-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  // ワークスペースを作成
  const { data: workspaceData, error: workspaceError } = await supabase
    .from('workspaces')
    .insert({
      id: workspaceId,
      name: name.trim(),
      type,
      owner_id: session.user.id,
    })
    .select()
    .single()

  if (workspaceError) {
    console.error('[supabase-client] Error creating workspace:', workspaceError)
    throw new Error(workspaceError.message)
  }

  // 作成者をメンバーとして追加
  const { error: memberError } = await supabase
    .from('workspace_members')
    .insert({
      workspace_id: workspaceId,
      user_id: session.user.id,
      role: 'owner',
    })

  if (memberError) {
    console.error('[supabase-client] Error adding workspace member:', memberError)
    // ワークスペースを削除してロールバック
    await supabase.from('workspaces').delete().eq('id', workspaceId)
    throw new Error(memberError.message)
  }

  // チームの場合は招待リンクも自動作成
  if (type === 'team') {
    await supabase
      .from('workspace_invitations')
      .insert({
        workspace_id: workspaceId,
        token: generateInviteToken(),
        created_by: session.user.id,
      })
  }

  return {
    id: workspaceData.id,
    name: workspaceData.name,
    type: workspaceData.type,
    ownerId: workspaceData.owner_id,
    createdAt: workspaceData.created_at,
  }
}

// 招待トークンからワークスペース情報を取得
// 未認証ユーザーでも呼び出し可能（招待リンクから参加するため）
export async function getWorkspaceByInviteToken(token: string): Promise<{
  workspace: Workspace
  invitation: WorkspaceInvitation
} | null> {
  const supabase = getSupabase()

  console.log('[invite] verifying token:', token)

  const { data, error, status, statusText } = await supabase
    .from('workspace_invitations')
    .select(`
      id,
      workspace_id,
      token,
      created_by,
      created_at,
      expires_at,
      workspaces (
        id,
        name,
        type,
        owner_id,
        created_at
      )
    `)
    .eq('token', token)
    .maybeSingle()

  console.log('[invite] response:', {
    status,
    statusText,
    hasData: !!data,
    data,
    error,
    errorMessage: error?.message,
    errorCode: error?.code,
    errorDetails: error?.details,
    errorHint: error?.hint,
  })

  if (error) {
    console.error('[invite] supabase error:', JSON.stringify(error, null, 2))
    throw error
  }
  if (!data) {
    console.warn('[invite] no row matched token')
    return null
  }

  // 有効期限チェック
  if (data.expires_at && new Date(data.expires_at) < new Date()) {
    console.warn('[invite] token expired:', data.expires_at)
    return null
  }

  // workspaces のネスト取得結果は配列で返るケースもあるため正規化
  const workspaceRaw: any = Array.isArray((data as any).workspaces)
    ? (data as any).workspaces[0]
    : (data as any).workspaces

  if (!workspaceRaw) {
    console.warn('[invite] invitation row found but workspaces is empty (RLS may be blocking workspaces SELECT)')
    return null
  }

  return {
    workspace: {
      id: workspaceRaw.id,
      name: workspaceRaw.name,
      type: workspaceRaw.type,
      ownerId: workspaceRaw.owner_id,
      createdAt: workspaceRaw.created_at,
    },
    invitation: {
      id: data.id,
      workspaceId: data.workspace_id,
      token: data.token,
      createdBy: data.created_by,
      createdAt: data.created_at,
      expiresAt: data.expires_at,
    },
  }
}

// ワークスペースに参加
export async function joinWorkspace(workspaceId: string): Promise<{ success: boolean }> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  // 既に参加しているかチェック
  const { data: existing } = await supabase
    .from('workspace_members')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('user_id', session.user.id)
    .maybeSingle()

  if (existing) {
    // 既に参加済み
    return { success: true }
  }

  const { error } = await supabase
    .from('workspace_members')
    .insert({
      workspace_id: workspaceId,
      user_id: session.user.id,
      role: 'member',
    })

  if (error) {
    console.error('[supabase-client] Error joining workspace:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

// ワークスペースから退出
export async function leaveWorkspace(workspaceId: string): Promise<{ success: boolean }> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { error } = await supabase
    .from('workspace_members')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('user_id', session.user.id)

  if (error) {
    console.error('[supabase-client] Error leaving workspace:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

// ワークスペースメンバー一覧を取得
export async function getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  // RPC で auth.users の表示名/メールを解決したメンバー一覧を取得する。
  // (workspace_members テーブルは user_id しか持たず、auth.users はクライアントから
  //  直接読めないため。関数側で「呼び出し元が同ワークスペースのメンバーか」を検証する)
  const { data, error } = await supabase
    .rpc('get_workspace_members_with_profile', { p_workspace_id: workspaceId })

  if (error) {
    console.error('[supabase-client] Error fetching workspace members:', error)
    throw new Error(error.message)
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    workspaceId: row.workspace_id,
    userId: row.user_id,
    role: row.role,
    joinedAt: row.joined_at,
    email: row.email ?? undefined,
    displayName: row.display_name ?? undefined,
  }))
}

// ワークスペースからメンバーを削除
export async function removeWorkspaceMember(workspaceId: string, userId: string): Promise<void> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { error } = await supabase.rpc('remove_workspace_member', {
    p_workspace_id: workspaceId,
    p_user_id: userId,
  })

  if (error) {
    console.error('[supabase-client] Error removing workspace member:', error)
    throw new Error(error.message)
  }
}

// 現在のユーザーIDを取得
export async function getCurrentUserId(): Promise<string | null> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  return session?.user?.id ?? null
}

// 招待を取得
export async function getWorkspaceInvitation(workspaceId: string): Promise<WorkspaceInvitation | null> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { data, error } = await supabase
    .from('workspace_invitations')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    console.error('[supabase-client] Error fetching workspace invitation:', error)
    return null
  }

  if (!data) {
    return null
  }

  return {
    id: data.id,
    workspaceId: data.workspace_id,
    token: data.token,
    createdBy: data.created_by,
    createdAt: data.created_at,
    expiresAt: data.expires_at,
  }
}

// 招待を作成
export async function createWorkspaceInvitation(workspaceId: string): Promise<WorkspaceInvitation> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { data, error } = await supabase
    .from('workspace_invitations')
    .insert({
      workspace_id: workspaceId,
      token: generateInviteToken(),
      created_by: session.user.id,
    })
    .select()
    .single()

  if (error) {
    console.error('[supabase-client] Error creating workspace invitation:', error)
    throw new Error(error.message)
  }

  return {
    id: data.id,
    workspaceId: data.workspace_id,
    token: data.token,
    createdBy: data.created_by,
    createdAt: data.created_at,
    expiresAt: data.expires_at,
  }
}

// 招待を再生成（古い招待を削除して新しく作成）
export async function regenerateWorkspaceInvitation(workspaceId: string): Promise<WorkspaceInvitation> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  // 既存の招待を削除
  await supabase
    .from('workspace_invitations')
    .delete()
    .eq('workspace_id', workspaceId)

  // 新しい招待を作成
  return createWorkspaceInvitation(workspaceId)
}

// メールで招待を送信
export async function sendInvitationEmail(
  workspaceId: string,
  workspaceName: string,
  email: string,
  inviterName?: string
): Promise<{ success: boolean; error?: string }> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { data, error } = await supabase.functions.invoke('send-invitation-email', {
    body: {
      email,
      workspaceId,
      workspaceName,
      inviterName,
    },
  })

  if (error) {
    console.error('[supabase-client] Error sending invitation email:', error)
    return { success: false, error: error.message }
  }

  if (data?.error) {
    return { success: false, error: data.error }
  }

  return { success: true }
}

// 問い合わせ（お問い合わせフォーム）。定義は lib/inquiry.ts、送信元の判定はここ
export type { InquiryCategory } from "@/lib/inquiry";

export async function submitInquiry(input: InquiryDraft): Promise<{ success: boolean; error?: string }> {
  const supabase = getSupabase();
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    return { success: false, error: "Unauthorized" };
  }

  const { data, error } = await supabase.functions.invoke("submit-inquiry", {
    body: {
      category: input.category,
      subject: input.subject,
      body: input.body,
      // Discord 通知に「どのアプリから来たか」を出すための補助情報 (DB には保存されない)
      source: (isTauri() ? "desktop" : "web") satisfies InquirySource,
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? "",
    },
  });

  if (error) {
    console.error("[supabase-client] Error submitting inquiry:", error);
    return { success: false, error: error.message };
  }

  if (data?.error) {
    return { success: false, error: data.error };
  }

  return { success: true };
}

// ワークスペース名を更新
export async function updateWorkspace(
  workspaceId: string,
  updates: { name?: string }
): Promise<Workspace> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const updateData: { name?: string } = {}
  if (updates.name !== undefined) updateData.name = updates.name.trim()

  const { data, error } = await supabase
    .from('workspaces')
    .update(updateData)
    .eq('id', workspaceId)
    .select()
    .single()

  if (error) {
    console.error('[supabase-client] Error updating workspace:', error)
    throw new Error(error.message)
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type,
    ownerId: data.owner_id,
    createdAt: data.created_at,
  }
}

// ワークスペースを削除
// ノートブック(と中の lines / completed_tasks / notebook_comments)、タグ、メンバー、招待は
// FK の ON DELETE CASCADE で DB 側が一緒に消す。ここで個別に消さないこと
// (notebooks は 20260904091500、tags は 20260904002200 で CASCADE に張り替え済み)。
// ローカル状態は workspace-store が個人ワークスペースへ切り替え、app-page の
// currentWorkspaceId 監視が notebooks を読み直すため、ここでの後始末は不要。
export async function deleteWorkspace(workspaceId: string): Promise<{ success: boolean }> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { error } = await supabase
    .from('workspaces')
    .delete()
    .eq('id', workspaceId)
    .eq('owner_id', session.user.id) // オーナーのみ削除可能

  if (error) {
    console.error('[supabase-client] Error deleting workspace:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

// 個人ワークスペースの自動作成（存在しない場合のみ）
export async function ensurePersonalWorkspace(): Promise<Workspace> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  // 既存の個人ワークスペースをチェック
  const workspaces = await getWorkspaces()
  const personalWorkspace = workspaces.find(w => w.type === 'personal')

  if (personalWorkspace) {
    return personalWorkspace
  }

  // 存在しない場合は作成
  return createWorkspace('Personal', 'personal')
}

// Claude の activity_log 取得のキャッシュ + インフライト重複排除
// タスク詳細サイドバーが高頻度で再マウント/再レンダリングされる状況で
// 同じ taskId を無限に fetch する事故を防ぐ。
const ACTIVITY_LOG_CACHE_TTL_MS = 5000
const activityLogCache = new Map<string, { at: number; data: ClaudeActivityLog[] }>()
const activityLogInFlight = new Map<string, Promise<ClaudeActivityLog[]>>()

// Claude の activity_log を取得 (タスク詳細のタイムライン用)
export async function getClaudeActivityLog(taskId: string): Promise<ClaudeActivityLog[]> {
  const now = Date.now()

  const cached = activityLogCache.get(taskId)
  if (cached && now - cached.at < ACTIVITY_LOG_CACHE_TTL_MS) {
    return cached.data
  }

  const existing = activityLogInFlight.get(taskId)
  if (existing) {
    return existing
  }

  const supabase = getSupabase()
  const promise = (async () => {
    try {
      const { data, error } = await supabase
        .from('claude_activity_log')
        .select('*')
        .eq('task_id', taskId)
        .order('created_at', { ascending: true })
      if (error) {
        console.error('[supabase-client] Error fetching activity log:', error)
        throw new Error(error.message)
      }
      const mapped = (data ?? []).map((row: any) => ({
        id: row.id,
        taskId: row.task_id,
        userId: row.user_id,
        tool: row.tool,
        summary: row.summary,
        metadata: row.metadata ?? undefined,
        createdAt: row.created_at,
      }))
      activityLogCache.set(taskId, { at: Date.now(), data: mapped })
      return mapped
    } finally {
      activityLogInFlight.delete(taskId)
    }
  })()

  activityLogInFlight.set(taskId, promise)
  return promise
}

// ユーザーが UI からタスクにコメントを追加 (assignee=user として task-note を挿入)
export async function addUserCommentToTask(
  taskId: string,
  notebookId: string,
  body: string
): Promise<Line> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized - no valid session")
  }

  // 同じ parent の max line_order を取得して +1
  const { data: maxRows } = await supabase
    .from('lines')
    .select('line_order')
    .eq('parent_task_id', taskId)
    .order('line_order', { ascending: false })
    .limit(1)
  const nextOrder = ((maxRows?.[0]?.line_order ?? 0) as number) + 1

  const now = Date.now()
  const row = {
    id: crypto.randomUUID(),
    notebook_id: notebookId,
    type: 'task-note',
    content: body,
    parent_task_id: taskId,
    assignee: 'user',
    created_at: now,
    line_order: nextOrder,
    // status は NULL → コメント扱い
  }

  const { data, error } = await supabase
    .from('lines')
    .insert(row)
    .select()
    .single()
  if (error) {
    console.error('[supabase-client] Error inserting user comment:', error)
    throw new Error(error.message)
  }

  // 既存 getLines の row→Line マッピングを inline で踏襲
  return {
    id: data.id,
    type: data.type,
    content: data.content || '',
    status: data.status,
    duration: data.duration,
    parentTaskId: data.parent_task_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    bulletPoint: data.bullet_point,
    indentLevel: data.indent_level,
    listNumber: data.list_number,
    scheduledDate: data.scheduled_date,
    startedAt: data.started_at,
    notes: data.notes || '',
    priority: data.priority || undefined,
    tags: data.tags || [],
    assignee: data.assignee ?? undefined,
    assigneeUserId: data.assignee_user_id ?? undefined,
    repeat: parseRepeatFromRow(data.repeat),
    reminder: parseReminderFromRow(data.reminder),
    notebookId: data.notebook_id,
  }
}

// ===== ノートブックコメント関連の関数 =====

// コメント取得（新しい順）
export async function fetchComments(notebookId: string): Promise<Comment[]> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { data, error } = await supabase
    .from('notebook_comments')
    .select('*')
    .eq('notebook_id', notebookId)
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('[supabase-client] Error fetching comments:', error)
    throw new Error(error.message)
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    notebookId: row.notebook_id,
    userId: row.user_id,
    content: row.content,
    createdAt: new Date(row.created_at).getTime(),
    updatedAt: new Date(row.updated_at).getTime(),
  }))
}

// コメント作成
export async function createComment(notebookId: string, content: string): Promise<Comment> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { data, error } = await supabase
    .from('notebook_comments')
    .insert({
      notebook_id: notebookId,
      user_id: session.user.id,
      content,
    })
    .select()
    .single()

  if (error) {
    console.error('[supabase-client] Error creating comment:', error)
    throw new Error(error.message)
  }

  return {
    id: data.id,
    notebookId: data.notebook_id,
    userId: data.user_id,
    content: data.content,
    createdAt: new Date(data.created_at).getTime(),
    updatedAt: new Date(data.updated_at).getTime(),
  }
}

// コメント更新
export async function updateComment(commentId: string, content: string): Promise<Comment> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { data, error } = await supabase
    .from('notebook_comments')
    .update({ content, updated_at: new Date().toISOString() })
    .eq('id', commentId)
    .eq('user_id', session.user.id)
    .select()
    .single()

  if (error) {
    console.error('[supabase-client] Error updating comment:', error)
    throw new Error(error.message)
  }

  return {
    id: data.id,
    notebookId: data.notebook_id,
    userId: data.user_id,
    content: data.content,
    createdAt: new Date(data.created_at).getTime(),
    updatedAt: new Date(data.updated_at).getTime(),
  }
}

// コメント削除
export async function deleteComment(commentId: string): Promise<{ success: boolean }> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error("Unauthorized")
  }

  const { error } = await supabase
    .from('notebook_comments')
    .delete()
    .eq('id', commentId)
    .eq('user_id', session.user.id)

  if (error) {
    console.error('[supabase-client] Error deleting comment:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

// ==========================================
// タスク画像 (task-images バケット)
// ==========================================

const TASK_IMAGES_BUCKET = "task-images"
const TASK_IMAGE_SIGNED_URL_TTL_SECONDS = 3600

// 署名URLのメモリキャッシュ。TTL の 90% を有効期限として再利用し、
// 同じ画像の NodeView 再マウントごとの createSignedUrl 呼び出しを避ける。
const taskImageSignedUrlCache = new Map<string, { url: string; expiresAt: number }>()

/**
 * タスク画像をアップロードし、ストレージパス（Markdown に保存する値）を返す。
 * パス形式: <notebookId>/<lineId>/<uuid>.<ext>
 */
export async function uploadTaskImage(
  notebookId: string,
  lineId: string,
  file: File
): Promise<{ path: string }> {
  const validation = validateTaskImageFile(file)
  if (!validation.ok) {
    throw new Error(
      validation.reason === "type"
        ? "対応していない画像形式です"
        : "画像サイズが上限を超えています"
    )
  }
  const supabase = getSupabase()
  const path = buildTaskImagePath(notebookId, lineId, validation.ext)
  console.log("[supabase-client] uploadTaskImage:", path)
  const { error } = await supabase.storage
    .from(TASK_IMAGES_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false })
  if (error) {
    console.error("[supabase-client] uploadTaskImage error:", error)
    throw error
  }
  return { path }
}

/**
 * タスク添付ドキュメント (pdf/docx/xlsx/pptx/txt/md/csv) をアップロードし、
 * ストレージパス（Markdown に保存する値）を返す。バケットは画像と同じ task-images。
 * contentType はブラウザ報告値ではなく正規化した MIME を明示する
 * (.md 等で File.type が空になる環境でもバケットの MIME 制限を通すため)。
 */
export async function uploadTaskFile(
  notebookId: string,
  lineId: string,
  file: File
): Promise<{ path: string }> {
  const validation = validateTaskFileFile(file)
  if (!validation.ok) {
    throw new Error(
      validation.reason === "type"
        ? "対応していないファイル形式です"
        : "ファイルサイズが上限を超えています"
    )
  }
  const supabase = getSupabase()
  const path = buildTaskImagePath(notebookId, lineId, validation.ext)
  console.log("[supabase-client] uploadTaskFile:", path)
  const { error } = await supabase.storage
    .from(TASK_IMAGES_BUCKET)
    .upload(path, file, { contentType: validation.contentType, upsert: false })
  if (error) {
    console.error("[supabase-client] uploadTaskFile error:", error)
    throw error
  }
  return { path }
}

/**
 * メモ画像をアップロードし、ストレージパス（Markdown に保存する値）を返す。
 * パス形式: memo/<userId>/<memoId>/<uuid>.<ext>（RLS は userId セグメントで本人判定）
 */
export async function uploadMemoImage(memoId: string, file: File): Promise<{ path: string }> {
  const validation = validateTaskImageFile(file)
  if (!validation.ok) {
    throw new Error(
      validation.reason === "type"
        ? "対応していない画像形式です"
        : "画像サイズが上限を超えています"
    )
  }
  const { supabase, userId } = await requireSessionUser()
  const path = buildMemoImagePath(userId, memoId, validation.ext)
  console.log("[supabase-client] uploadMemoImage:", path)
  const { error } = await supabase.storage
    .from(TASK_IMAGES_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false })
  if (error) {
    console.error("[supabase-client] uploadMemoImage error:", error)
    throw error
  }
  return { path }
}

/** ストレージパスから短命の署名URLを取得する（RLS によりアクセス権が無ければ失敗する）。 */
export async function getTaskImageSignedUrl(path: string): Promise<string> {
  const cached = taskImageSignedUrlCache.get(path)
  if (cached && cached.expiresAt > Date.now()) return cached.url

  const supabase = getSupabase()
  const { data, error } = await supabase.storage
    .from(TASK_IMAGES_BUCKET)
    .createSignedUrl(path, TASK_IMAGE_SIGNED_URL_TTL_SECONDS)
  if (error || !data?.signedUrl) {
    console.error("[supabase-client] getTaskImageSignedUrl error:", error)
    throw error ?? new Error("署名URLの取得に失敗しました")
  }
  taskImageSignedUrlCache.set(path, {
    url: data.signedUrl,
    expiresAt: Date.now() + TASK_IMAGE_SIGNED_URL_TTL_SECONDS * 0.9 * 1000,
  })
  return data.signedUrl
}

// ============ 振り返りメモ (retrospectives) ============

async function requireSessionUser() {
  const supabase = getSupabase()
  const { data: { session }, error: sessionError } = await supabase.auth.getSession()
  if (sessionError) {
    console.error('[supabase-client] Session error:', sessionError)
    throw new Error(`Session error: ${sessionError.message}`)
  }
  if (!session?.user) {
    throw new Error("Unauthorized - no valid session")
  }
  return { supabase, userId: session.user.id }
}

// 指定期間の振り返りを1件取得 (無ければ null)
//
// scope.workspaceId は必須。省略を許すと user_id × mode × period_start だけの
// 検索になり、同じユーザーの個人 WS とチーム WS の振り返りが同じ 1 行を
// 奪い合う (WS を切り替えても同じ内容が出て、片方の保存がもう片方を潰す)。
export async function getRetrospective(
  mode: RetroMode,
  periodStart: string,
  scope: RetroScope
): Promise<RetrospectiveRecord | null> {
  // セッション取得より前に検証する (配線ミスを I/O 前に落とす)
  const { workspaceId, targetUserId: scopedUserId } = assertRetroScope(
    scope,
    'getRetrospective'
  )
  const { supabase, userId } = await requireSessionUser()
  const targetUserId = scopedUserId ?? userId

  let query = supabase
    .from('retrospectives')
    .select('*')
    .eq('user_id', targetUserId)
    .eq('mode', mode)
    .eq('period_start', periodStart)
  // 個人 WS の行は workspace_id が null (20260723000000 以前から null で保存されている)
  query = workspaceId === null
    ? query.is('workspace_id', null)
    : query.eq('workspace_id', workspaceId)

  const { data, error } = await query.maybeSingle()

  if (error) {
    console.error('[supabase-client] Error fetching retrospective:', error)
    throw new Error(error.message)
  }
  return data ? mapRetrospectiveRow(data) : null
}

// 振り返りを upsert (user_id × mode × period_start × workspace_id で1件)
//
// onConflict は 20260904170000 で張り替えた
// `unique nulls not distinct (user_id, mode, period_start, workspace_id)` を狙う。
// workspace_id を外すと個人 WS とチーム WS が同じ行を上書きし合う。
export async function upsertRetrospective(
  mode: RetroMode,
  periodStart: string,
  draft: RetroDraft,
  scope: RetroScope
): Promise<RetrospectiveRecord> {
  const { workspaceId, targetUserId: scopedUserId } = assertRetroScope(
    scope,
    'upsertRetrospective'
  )
  const { supabase, userId } = await requireSessionUser()
  const targetUserId = scopedUserId ?? userId

  const row: Record<string, unknown> = {
    user_id: targetUserId,
    mode,
    period_start: periodStart,
    keep: draft.keep,
    problem: draft.problem,
    try_next: draft.tryNext,
    free_note: draft.freeNote,
    updated_at: new Date().toISOString(),
    // 個人 WS は null を明示的に書く (書かないと既存行の値が残り、
    // 個人の保存がチームの行を上書きし得る)
    workspace_id: workspaceId,
  }

  const { data, error } = await supabase
    .from('retrospectives')
    .upsert(row, { onConflict: 'user_id,mode,period_start,workspace_id' })
    .select()
    .single()

  if (error) {
    console.error('[supabase-client] Error upserting retrospective:', error)
    throw new Error(error.message)
  }
  return mapRetrospectiveRow(data)
}

// ===== ユーザー設定 (user_settings) =====
// タイマー設定・アプリ設定など端末間で同期する好み設定。key 単位の last-write-wins。

// 自分の全設定を取得。未ログイン時は null (同期をスキップする合図)
export async function getUserSettings(): Promise<Record<
  string,
  { value: unknown; updatedAt: number }
> | null> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) return null

  const { data, error } = await supabase
    .from('user_settings')
    .select('key, value, updated_at')

  if (error) {
    console.error('[supabase-client] Error fetching user settings:', error)
    throw new Error(error.message)
  }

  const result: Record<string, { value: unknown; updatedAt: number }> = {}
  for (const row of data ?? []) {
    result[row.key] = {
      value: row.value,
      updatedAt: new Date(row.updated_at).getTime(),
    }
  }
  return result
}

// 設定を1件 upsert。未ログイン時は false (dirty のまま残して後で再送)。
// updated_at はクライアントの updatedAtMs をそのまま書き、ローカル側の
// タイムスタンプと厳密に一致させる (LWW 比較のクロックずれ対策)
export async function upsertUserSetting(
  key: string,
  value: unknown,
  updatedAtMs: number
): Promise<boolean> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) return false

  const { error } = await supabase
    .from('user_settings')
    .upsert(
      {
        user_id: session.user.id,
        key,
        value,
        updated_at: new Date(updatedAtMs).toISOString(),
      },
      { onConflict: 'user_id,key' }
    )

  if (error) {
    console.error('[supabase-client] Error upserting user setting:', error)
    throw new Error(error.message)
  }
  return true
}

// ==========================================
// 招待プレビュー (未ログインでも参加先の中身を見せる)
// ==========================================

// 招待トークンから参加先のプレビューを取得する。
// notebooks / lines の RLS は緩めず、SECURITY DEFINER の RPC が範囲を絞って返す
// (supabase/migrations/20260918120100_add_invite_preview_rpc.sql)。
// プレビューはあくまで任意情報なので、失敗しても例外は投げず null を返し、
// 参加フロー (getWorkspaceByInviteToken / joinWorkspaceByToken) を止めない。
export async function getInvitePreview(token: string): Promise<InvitePreview | null> {
  if (!token) return null
  const supabase = getSupabase()
  try {
    const { data, error } = await supabase.rpc('get_invite_preview', { p_token: token })
    if (error) {
      console.error('[invite] Error fetching invite preview:', error)
      return null
    }
    return parseInvitePreview(data)
  } catch (err) {
    console.error('[invite] Error fetching invite preview:', err)
    return null
  }
}

// 自分の subscriptions 行 (Premium 判定用)。RLS で自分の行だけ返る。
// mobile/src/lib/api.ts の getMySubscription と同じクエリだが、戻り値は
// 「成功 (行あり/なし)」と「失敗 (ネットワーク等)」を区別する三値。
// 失敗を「行なし = 未購入」と同一視すると、オフライン起動で購入済みユーザーの
// premium キャッシュを none で上書きしてペイウォールに締め出してしまう
// (premium-provider は ok:false のとき判定を確定させず、キャッシュも書かない)
export type MySubscriptionResult =
  | { ok: true; subscription: { plan_type: string; status: string } | null }
  | { ok: false }

export async function getMySubscription(): Promise<MySubscriptionResult> {
  try {
    const supabase = getSupabase()
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) return { ok: true, subscription: null }

    const { data, error } = await supabase
      .from('subscriptions')
      .select('plan_type, status')
      .eq('user_id', session.user.id)
      .maybeSingle()

    if (error) {
      console.error('[supabase-client] Error fetching subscription:', error.message)
      return { ok: false }
    }
    return { ok: true, subscription: data }
  } catch {
    return { ok: false }
  }
}
